import { Divider, Header, Segment, Table } from 'semantic-ui-react';

const BillingPeriodStatementView = (statement: Statement) => {
  function camelCaseToWords(s: string) {
    const result = s.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  const renderMessages = (messages) => (
    <Segment>
      <Header as="h2">Messages</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Header</Table.HeaderCell>
            <Table.HeaderCell>Priority</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
            {messages?.Header?.length > 0 ? 
              (messages?.Header.map((msg, index) => <Table.Row><Table.Cell>{msg.key}</Table.Cell><Table.Cell>{msg.priority}</Table.Cell></Table.Row>)) : 
              (<Table.Row><Table.Cell>{'No messages'}</Table.Cell></Table.Row>)
            }
        </Table.Body>
      </Table>

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Payment box</Table.HeaderCell>
            <Table.HeaderCell>Priority</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
            {messages?.PaymentBox?.length > 0 ? 
              (messages?.PaymentBox.map((msg, index) =><Table.Row><Table.Cell>{msg.key}</Table.Cell><Table.Cell>{msg.priority}</Table.Cell></Table.Row>)) : 
              (<Table.Row><Table.Cell>{'No messages'}</Table.Cell></Table.Row>)
            }
        </Table.Body>
      </Table>

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Body</Table.HeaderCell>
            <Table.HeaderCell>Priority</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
            {messages?.Body?.length > 0 ? 
              (messages?.Body.map((msg, index) => <Table.Row><Table.Cell>{msg.key}</Table.Cell><Table.Cell>{msg.priority}</Table.Cell></Table.Row>)) : 
              (<Table.Row><Table.Cell>{'No messages'}</Table.Cell></Table.Row>)
            }
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderAccountDetails = (accountDetails) => (
    <Segment>
      <Header as="h3">Account Details</Header>
      <Table celled>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Account ID</Table.Cell>
            <Table.Cell>{accountDetails.accountId}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Product ID</Table.Cell>
            <Table.Cell>{accountDetails.productId}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Status</Table.Cell>
            <Table.Cell>{accountDetails.status}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Opening Date</Table.Cell>
            <Table.Cell>{new Date(accountDetails.openingDate).toLocaleString()}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Account Number</Table.Cell>
            <Table.Cell>{accountDetails.accountNumber}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Credit Limit</Table.Cell>
            <Table.Cell>{`${accountDetails.creditLimitAmount.value} ${accountDetails.creditLimitAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Statement Type</Table.Cell>
            <Table.Cell>{accountDetails.statementType}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderStatementDetails = (statementDetails) => (
    <Segment>
      <Header as="h3">Statement Details</Header>
      <Table celled>
        <Table.Body>
          {statementDetails.previousStatementDate && (
            <Table.Row>
              <Table.Cell>Previous Statement Date</Table.Cell>
              <Table.Cell>{new Date(statementDetails.previousStatementDate).toLocaleString()}</Table.Cell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.Cell>Current Statement Date</Table.Cell>
            <Table.Cell>{new Date(statementDetails.currentStatementDate).toLocaleString()}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Cycle Date</Table.Cell>
            <Table.Cell>{new Date(statementDetails.cycleDate).toLocaleString()}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderBalances = (balances) => (
    <Segment>
      <Header as="h3">Balances</Header>
      <Table celled>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Previous Period Closing Amount</Table.Cell>
            <Table.Cell>{`${balances.previousPeriodClosingAmount.value} ${balances.previousPeriodClosingAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Current Period Closing Amount</Table.Cell>
            <Table.Cell>{`${balances.currentPeriodClosingAmount.value} ${balances.currentPeriodClosingAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Over Limit Amount</Table.Cell>
            <Table.Cell>{`${balances.overLimitAmount.value} ${balances.overLimitAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Available Credit</Table.Cell>
            <Table.Cell>{`${balances.availableCredit.value} ${balances.availableCredit.currencyCode}`}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderBillingPeriod = (billingPeriod) => (
    <Segment>
      <Header as="h3">Billing Period</Header>
      <Table celled>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Billing Period ID</Table.Cell>
            <Table.Cell>{billingPeriod.billingPeriodId}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Start Date</Table.Cell>
            <Table.Cell>{new Date(billingPeriod.startDate).toLocaleString()}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>End Date</Table.Cell>
            <Table.Cell>{new Date(billingPeriod.endDate).toLocaleString()}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Payment Due Date</Table.Cell>
            <Table.Cell>{new Date(billingPeriod.paymentRequested.paymentDueDate).toLocaleString()}</Table.Cell>
          </Table.Row>
          {billingPeriod.paymentRequested?.totalAmount && (
            <Table.Row>
              <Table.Cell>Total Amount</Table.Cell>
              <Table.Cell>{`${billingPeriod.paymentRequested.totalAmount.value} ${billingPeriod.paymentRequested.totalAmount.currencyCode}`}</Table.Cell>
            </Table.Row>
          )}
          {billingPeriod.paymentRequested?.minimumDue?.totalAmount && (
            <Table.Row>
              <Table.Cell>Minimum Due</Table.Cell>
              <Table.Cell>{`${billingPeriod.paymentRequested.minimumDue.totalAmount.value} ${billingPeriod.paymentRequested.minimumDue.totalAmount.currencyCode}`}</Table.Cell>
            </Table.Row>
          )}
          {billingPeriod.paymentRequested?.minimumDue?.arrearsAmount && (
            <Table.Row>
              <Table.Cell>Arrears Amount</Table.Cell>
              <Table.Cell>{`${billingPeriod.paymentRequested.minimumDue.arrearsAmount.value} ${billingPeriod.paymentRequested.minimumDue.arrearsAmount.currencyCode}`}</Table.Cell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.Cell>Estimated Interest Amount</Table.Cell>
            <Table.Cell>{`${billingPeriod.estimatedInterestAmount.value} ${billingPeriod.estimatedInterestAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Arrears Level</Table.Cell>
            <Table.Cell>{billingPeriod.arrearsLevel}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderInterestRates = (interestRates) => (
    <Segment>
      <Header as="h3">Interest Rates</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Spend Type</Table.HeaderCell>
            <Table.HeaderCell>Balance Amount</Table.HeaderCell>
            <Table.HeaderCell>Annual Interest Rate (AIR)</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Expiry Date</Table.HeaderCell>
            <Table.HeaderCell>Go To Rate</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {interestRates.map((rate, index) => (
            <Table.Row key={index}>
              <Table.Cell>{rate.spendType}</Table.Cell>
              <Table.Cell>{`${rate.balanceAmount.value} ${rate.balanceAmount.currencyCode}`}</Table.Cell>
              <Table.Cell>{rate.air}%</Table.Cell>
              <Table.Cell>{rate.description}</Table.Cell>
              <Table.Cell>{rate.expiryDate ? new Date(rate.expiryDate).toLocaleString() : 'N/A'}</Table.Cell>
              <Table.Cell>{rate.goToRate ? `${rate.goToRate}%` : 'N/A'}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );

  const renderTotals = (transactions) => (
    <Segment>
      <Header as="h3">Totals</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Object.keys(transactions)
            .filter(
              (key) =>
                key.endsWith('Amount') &&
                key !== 'debitsTotalTransactionsAmount' &&
                key !== 'creditsTotalTransactionsAmount'
            )
            .map((key, index) => (
              <Table.Row key={index}>
                <Table.Cell>{camelCaseToWords(key)}</Table.Cell>
                <Table.Cell>{`${transactions[key].value} ${transactions[key].currencyCode}`}</Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
      <Divider />
      <Header as="h3">Transaction Totals</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Debits Total Transactions Amount</Table.Cell>
            <Table.Cell>{`${transactions.debitsTotalTransactionsAmount.value} ${transactions.debitsTotalTransactionsAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Credits Total Transactions Amount</Table.Cell>
            <Table.Cell>{`${transactions.creditsTotalTransactionsAmount.value} ${transactions.creditsTotalTransactionsAmount.currencyCode}`}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Divider />
      {transactions.fees?.some((fee) => fee.amount.value !== 0) && (
        <>
          <Header as="h3">Fees</Header>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Amount</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {transactions.fees?.filter(fee => fee.amount.value !==0).map((fee, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{fee.category}</Table.Cell>
                  <Table.Cell>{`${fee.amount.value} ${fee.amount.currencyCode}`}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </Segment>
  );

  const renderTransactions = (transactions) => (
    <Segment>
      <Header as="h3">Transactions Items</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Posted Date</Table.HeaderCell>
            <Table.HeaderCell>Effective Date</Table.HeaderCell>
            <Table.HeaderCell>Balance Movement Type</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Spend Type</Table.HeaderCell>
            <Table.HeaderCell>Direction</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {transactions.items.map((transaction, index) => (
            <Table.Row key={index}>
              <Table.Cell>{new Date(transaction.postedDate).toLocaleString()}</Table.Cell>
              <Table.Cell>{new Date(transaction.effectiveDate).toLocaleString()}</Table.Cell>
              <Table.Cell>{transaction.balanceMovementType}</Table.Cell>
              <Table.Cell>{`${transaction.amount.value} ${transaction.amount.currencyCode}`}</Table.Cell>
              <Table.Cell>{transaction.description}</Table.Cell>
              <Table.Cell>{transaction.spendType}</Table.Cell>
              <Table.Cell>{transaction.direction}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );

  return (
    <>
      {statement.data?.accountDetails && renderAccountDetails(statement.data?.accountDetails)}
      {statement.data?.statementDetails && renderStatementDetails(statement.data?.statementDetails)}
      {statement.data?.balances && renderBalances(statement.data?.balances)}
      {statement.data?.billingPeriod && renderBillingPeriod(statement.data.billingPeriod)}
      {statement.data?.interestRates && renderInterestRates(statement.data.interestRates)}
      {statement.data?.transactions && renderTotals(statement.data.transactions)}
      {statement.data?.transactions && renderTransactions(statement.data.transactions)}
      {statement.data?.messages && renderMessages(statement.data?.messages)}
    </>
  );
};

export default BillingPeriodStatementView;
